import { getFormattedLabel } from "@/operations/screensizes";
import { Chart, registerables, ScriptableContext, Tooltip } from "chart.js";
import { CrosshairPlugin } from "chartjs-plugin-crosshair";
import gradient from "chartjs-plugin-gradient";
import dayjs from "dayjs";
import { Component, Prop, Vue, Watch } from "nuxt-property-decorator";
import type { PelletsChartPoint } from "~/types/Charts";

let chartGlobalsRegistered = false;
function registerChartGlobalsOnce() {
  if (process.server) return;
  if (chartGlobalsRegistered) return;
  chartGlobalsRegistered = true;
  Chart.register(...registerables);
  Chart.register(gradient);
  Chart.register(CrosshairPlugin);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  Tooltip.positioners.custom = function (_elements, eventPosition) {
    return { x: eventPosition.x, y: eventPosition.y };
  };
}

export interface GradientStop {
  position: number;
  opacity: number;
}

export interface DatasetColors {
  rgb: string;
  gradientStops: GradientStop[];
}

export const PELLETS_CHART_COLORS: {
  comparisonColors: DatasetColors;
  mainColors: DatasetColors;
} = {
  comparisonColors: {
    rgb: "253, 200, 53",
    gradientStops: [
      { position: 0, opacity: 0 },
      { position: 1, opacity: 0.2 },
    ],
  },
  mainColors: {
    rgb: "137, 188, 37",
    gradientStops: [
      { position: 0, opacity: 0 },
      { position: 0.525, opacity: 0 },
      { position: 1, opacity: 0.3 },
    ],
  },
};

export type ChartSeries = Array<PelletsChartPoint>;

export interface PelletsChartTheme {
  comparisonColors: DatasetColors;
  mainColors: DatasetColors;
  axis: {
    fontSize: number;
    fontWeight?: string;
    color: string;
  };
  yAxis: {
    beginAtZero: boolean;
    stepSize: number;
    grace?: string | number;
  };
  pointRadius?: number;
  xAxisMaxTicks: number;
  legend: {
    display?: boolean;
    pointStyleWidth: number;
    fontSize: number;
  };
  layout: {
    aspectRatio: number | undefined;
    maintainAspectRatio: boolean;
  };
}

@Component
export default class PelletsChartBase extends Vue {
  @Prop({ required: true }) canvasID!: string;
  @Prop() yAchsesLabel!: string;
  @Prop({ default: () => [] }) chartDataLabels!: Array<string | number>;
  @Prop({ default: "Sackware" }) mainChartDataLabel!: string;
  @Prop({ default: () => [] }) mainChartDataSeries!: ChartSeries;
  @Prop({ default: "Lose Ware" }) comparisonChartDataLabel!: string;
  @Prop({ default: () => [] }) comparisonChartDataSeries!: ChartSeries;

  chartInstance: Chart | null = null;
  viewportWidth = 1024;
  private tooltipRafId: number | null = null;
  private resizeHandler: (() => void) | null = null;
  private resizeDebounceId: ReturnType<typeof setTimeout> | null = null;

  // Subclasses must override this
  get chartTheme(): PelletsChartTheme {
    throw new Error("chartTheme must be implemented by subclass");
  }

  get tooltipId(): string {
    return `${this.canvasID}-tooltip`;
  }

  tooltipValueId(index: number): string {
    return `${this.canvasID}-tooltip-value-${index}`;
  }

  tooltipColorId(index: number): string {
    return `${this.canvasID}-tooltip-color-${index}`;
  }

  get hasData() {
    return (
      this.mainChartDataSeries?.length > 0 ||
      this.comparisonChartDataSeries?.length > 0 ||
      this.chartDataLabels?.length > 0
    );
  }

  getTooltipValueElements() {
    return [
      document.getElementById(this.tooltipValueId(0)),
      document.getElementById(this.tooltipValueId(1)),
    ];
  }

  getTooltipColorElements() {
    return [
      document.getElementById(this.tooltipColorId(0)),
      document.getElementById(this.tooltipColorId(1)),
    ];
  }

  createGradientFill(colorRgb: string, stops: GradientStop[]) {
    return function (context) {
      const chart = context.chart;
      const { ctx, chartArea } = chart;
      if (!chartArea) return null;
      const grad = ctx.createLinearGradient(
        0,
        chartArea.bottom,
        0,
        chartArea.top,
      );
      stops.forEach(({ position, opacity }) => {
        grad.addColorStop(position, `rgba(${colorRgb}, ${opacity})`);
      });
      return grad;
    };
  }

  getTooltip(chart) {
    const tooltip = document.getElementById(this.tooltipId);
    if (!tooltip) return null;

    const tooltipTitle = tooltip.querySelector(".js-chart-tooltip-title");
    if (tooltipTitle) {
      const title = chart.tooltip.title;
      tooltipTitle.textContent = Array.isArray(title) ? title.join(" ") : title;
    }

    if (chart.tooltip.x !== undefined) {
      const valueElements = this.getTooltipValueElements();
      valueElements.forEach((element, index) => {
        if (!element) return;
        element.textContent =
          chart.tooltip.dataPoints[index] === undefined
            ? ""
            : `${chart.tooltip.body[index].lines[0]}`;
      });
    }

    tooltip.style.top = chart.tooltip.y + 40 + "px";

    const xAlignLeft = window.innerWidth >= 680 ? 45 : 10;
    const { xAlign, yAlign } = chart.tooltip;
    let xOffset;

    if (xAlign === "left" && (yAlign === "bottom" || yAlign === "top")) {
      xOffset = xAlignLeft + 20;
    } else if (xAlign === "left") {
      xOffset = xAlignLeft;
    } else if (xAlign === "center") {
      xOffset = -95;
    } else {
      xOffset = -40;
    }

    tooltip.style.left = chart.tooltip.x + xOffset + "px";

    return tooltip;
  }

  externalTooltipHandler = (context) => {
    if (this.tooltipRafId) return;
    this.tooltipRafId = requestAnimationFrame(() => {
      this.tooltipRafId = null;
      this.updateTooltip(context);
    });
  };

  private updateTooltip(context) {
    const { chart, tooltip } = context;

    if (context.tooltip.x !== undefined) {
      const colorElements = this.getTooltipColorElements();
      colorElements.forEach((element, index) => {
        if (!element) return;
        element.style.backgroundColor =
          context.tooltip.labelColors[index] === undefined
            ? "transparent"
            : context.tooltip.labelColors[index].borderColor;
      });
    }

    const tooltipEl = this.getTooltip(chart);
    if (!tooltipEl) return;

    if (tooltip.opacity === 0 || tooltip.body[0]?.lines[0] === undefined) {
      tooltipEl.style.opacity = "0";
      tooltipEl.style.pointerEvents = "none";
    } else {
      tooltipEl.style.opacity = "1";
      tooltipEl.style.pointerEvents = "auto";
    }
  }

  get chartConfig() {
    const theme = this.chartTheme;
    const { comparisonColors, mainColors } = theme;
    const lastPointRadius = (context: ScriptableContext<"line">) => {
      const len = context.dataset.data.length;
      const isLast = context.dataIndex === len - 1;
      return isLast ? theme.pointRadius ?? 0 : 0;
    };

    const monthLabelCache = new Map<string, string>();
    const formatMonth = (index: number): string => {
      const raw = this.chartDataLabels[index];
      if (raw == null) {
        return "";
      }
      const key = String(raw);
      const cached = monthLabelCache.get(key);
      if (cached !== undefined) {
        return cached;
      }
      const parts = key.split(".");
      if (parts.length !== 3) return "";
      const formatted = dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`).format(
        "MMM",
      );
      monthLabelCache.set(key, formatted);
      return formatted;
    };

    return {
      type: "line",
      data: {
        labels: this.chartDataLabels,
        datasets: [
          {
            label: this.comparisonChartDataLabel,
            data: this.comparisonChartDataSeries,
            borderColor: `rgba(${comparisonColors.rgb}, 1)`,
            backgroundColor: this.createGradientFill(
              comparisonColors.rgb,
              comparisonColors.gradientStops,
            ),
            borderWidth: 3.5,
            pointBorderColor: `rgba(${comparisonColors.rgb}, 1)`,
            pointBackgroundColor: `rgba(${comparisonColors.rgb}, 1)`,
            pointRadius: lastPointRadius,
            pointHoverBorderColor: "#fff",
            pointHoverBorderWidth: 3,
            pointHoverRadius: 6,
            fill: true,
            tension: 0.4,
          },
          {
            label: this.mainChartDataLabel,
            data: this.mainChartDataSeries,
            borderColor: `rgba(${mainColors.rgb}, 1)`,
            backgroundColor: this.createGradientFill(
              mainColors.rgb,
              mainColors.gradientStops,
            ),
            borderWidth: 3.5,
            pointBorderColor: `rgba(${mainColors.rgb}, 1)`,
            pointBackgroundColor: `rgba(${mainColors.rgb}, 1)`,
            pointRadius: lastPointRadius,
            pointHoverBorderColor: "#fff",
            pointHoverBorderWidth: 3,
            pointHoverRadius: 6,
            fill: true,
            tension: 0.4,
          },
        ],
      },
      options: {
        aspectRatio: theme.layout.aspectRatio,
        maintainAspectRatio: theme.layout.maintainAspectRatio,
        responsive: true,
        spanGaps: true,
        layout: {
          padding: { left: 0 },
        },
        elements: {
          line: {
            cubicInterpolationMode: "monotone",
          },
        },
        title: {
          display: false,
        },
        hover: {
          mode: "index",
          intersect: false,
        },
        scales: {
          y: {
            position: "right",
            beginAtZero: theme.yAxis.beginAtZero,
            grace: theme.yAxis.grace ?? 0,
            ticks: {
              maxTicksLimit: 6,
              stepSize: theme.yAxis.stepSize,
              font: {
                size: theme.axis.fontSize,
                ...(theme.axis.fontWeight && {
                  weight: theme.axis.fontWeight,
                }),
                family: "Fira Sans",
              },
              color: theme.axis.color,
              callback(value, index) {
                if (index === 0) return "";
                return `${value} €`;
              },
            },
            grid: {
              color: "rgba(180, 185, 195, 0.8)",
              drawBorder: false,
              drawOnChartArea: true,
              drawTicks: true,
              tickMarkLength: 10,
            },
          },
          x: {
            type: "category",
            ticks: {
              autoSkip: true,
              maxTicksLimit: theme.xAxisMaxTicks,
              maxRotation: 0,
              font: {
                size: theme.axis.fontSize,
                ...(theme.axis.fontWeight && {
                  weight: theme.axis.fontWeight,
                }),
                family: "Fira Sans",
              },
              color: theme.axis.color,
              align: "start",
              callback: (context, index) =>
                index === 0 ? "" : formatMonth(context as number),
            },
            grid: {
              color: "rgba(180, 185, 195, 0.8)",
              drawBorder: true,
              drawOnChartArea: true,
              drawTicks: true,
              tickMarkLength: 10,
            },
          },
        },
        plugins: {
          tooltip: {
            enabled: false,
            external: this.externalTooltipHandler,
            position: "custom",
            mode: "index",
            intersect: false,
            displayColors: true,
            xPadding: 20,
            yPadding: 20,
            caretPadding: 6,
            cornerRadius: 5,
            borderWidth: 1,
            bodySpacing: 5,
            borderColor: "#000",
            callbacks: {
              label: function (tooltipItem) {
                const label = `${tooltipItem.formattedValue}`;
                const FormattedLabel = getFormattedLabel(
                  tooltipItem.dataset.label,
                );
                return [`${FormattedLabel}: ${label} €`];
              },
            },
          },
          legend: {
            display: theme.legend.display ?? false,
            position: "bottom" as const,
            align: "center" as const,
            labels: {
              usePointStyle: true,
              boxWidth: 0,
              padding: 20,
              pointStyle: "line",
              pointStyleWidth: theme.legend.pointStyleWidth,
              font: {
                size: theme.legend.fontSize,
                family: "Fira Sans",
                color: "rgba(0,0,0)",
              },
            },
          },
          crosshair: {
            sync: {
              enabled: false,
            },
            zoom: {
              enabled: false,
            },
            line: {
              color: "hsla(10, 1%, 1%, 1)",
              width: 1,
              dashPattern: [10, 3],
            },
          },
        },
      },
    };
  }

  mounted() {
    registerChartGlobalsOnce();
    this.viewportWidth = window.innerWidth;
    this.resizeHandler = () => {
      if (this.resizeDebounceId) clearTimeout(this.resizeDebounceId);
      this.resizeDebounceId = setTimeout(() => {
        this.resizeDebounceId = null;
        this.viewportWidth = window.innerWidth;
      }, 150);
    };
    window.addEventListener("resize", this.resizeHandler);
    this.createChart(this.canvasID, this.chartConfig);
    // When mounted inside <client-only> the canvas can briefly read 0×0
    // before the parent flex container settles. Force a resize on the next
    // frame so Chart.js picks up the real dimensions.
    requestAnimationFrame(() => {
      if (this.chartInstance) this.chartInstance.resize();
    });
  }

  beforeDestroy() {
    if (this.tooltipRafId) {
      cancelAnimationFrame(this.tooltipRafId);
      this.tooltipRafId = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.resizeDebounceId) {
      clearTimeout(this.resizeDebounceId);
      this.resizeDebounceId = null;
    }
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }

  @Watch("viewportWidth")
  onViewportWidthChange() {
    if (!this.chartInstance) return;
    this.createChart(this.canvasID, this.chartConfig);
  }

  private _chartUpdateScheduled!: boolean;

  created() {
    Object.defineProperty(this, "_chartUpdateScheduled", {
      value: false,
      writable: true,
      enumerable: false,
      configurable: true,
    });
  }

  scheduleChartUpdate() {
    if (process.server) return;
    if (this._chartUpdateScheduled) return;
    this._chartUpdateScheduled = true;
    this.$nextTick(() => {
      this._chartUpdateScheduled = false;
      this.applyChartUpdate();
    });
  }

  applyChartUpdate() {
    if (!this.chartInstance) {
      this.createChart(this.canvasID, this.chartConfig);
      return;
    }
    const { data } = this.chartInstance;
    data.labels = this.chartDataLabels;
    if (data.datasets[0])
      data.datasets[0].data = this.comparisonChartDataSeries;
    if (data.datasets[1]) data.datasets[1].data = this.mainChartDataSeries;
    this.chartInstance.update();
  }

  @Watch("chartDataLabels")
  @Watch("mainChartDataSeries")
  @Watch("comparisonChartDataSeries")
  onChartDataChange() {
    this.scheduleChartUpdate();
  }

  createChart(chartId: string, chartData: any) {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
    const ctx = document.getElementById(chartId) as HTMLCanvasElement | null;
    if (!ctx) return;
    this.chartInstance = new Chart(ctx, {
      type: chartData.type,
      data: chartData.data,
      options: chartData.options,
    });
  }
}
